/**
 * <s-swatch-preview> — Product card colour preview
 * Swatch buttons swap the card image to that colour's photo and repoint the
 * card links at that variant, so the product page opens on the chosen colour.
 * Hovering a swatch previews it; leaving the swatch row restores the selection.
 *
 * Markup contract (inside the element):
 *   [data-swatch-image="<variant id>"] — one hidden image per colour
 *   [data-swatch-link]                 — links whose href follows the selection
 *   button[command="--preview-swatch"][commandfor="<this id>"][data-variant-id][data-url]
 */
class SSwatchPreview extends HTMLElement {
  #ac;
  #selected = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // CommandEvent does not bubble; it is dispatched on this element via commandfor.
    this.addEventListener('command', (e) => {
      if (e.command !== '--preview-swatch' || !(e.source instanceof HTMLElement)) return;
      this.#select(e.source);
    }, opts);

    this.#buttons().forEach((button) => {
      button.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') this.#show(button.dataset.variantId);
      }, opts);
    });

    const row = this.querySelector('[data-swatch-row]');
    row?.addEventListener('pointerleave', () => {
      this.#show(this.#selected?.dataset.variantId ?? null);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #buttons() {
    return [...this.querySelectorAll('button[command="--preview-swatch"]')];
  }

  #select(button) {
    this.#selected = button;
    this.#buttons().forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    this.querySelectorAll('[data-swatch-link]').forEach((link) => {
      if (button.dataset.url) link.setAttribute('href', button.dataset.url);
    });
    this.querySelectorAll('[data-quick-add-id]').forEach((input) => {
      if (button.dataset.variantId) input.value = button.dataset.variantId;
    });
    this.#show(button.dataset.variantId);
  }

  #show(variantId) {
    this.querySelectorAll('[data-swatch-image]').forEach((img) => {
      const active = variantId !== null && img.dataset.swatchImage === variantId;
      img.classList.toggle('opacity-100', active);
      img.classList.toggle('opacity-0', !active);
    });
  }
}

if (!customElements.get('s-swatch-preview')) customElements.define('s-swatch-preview', SSwatchPreview);
